import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { storage, STORAGE_KEYS, generateId } from '../utils/storage';
import { useGymInfra } from './GymInfraContext';
import { useUI } from './UIContext';
import { supabase } from '../lib/supabase';
import {
  dbAddMembershipProduct, dbUpdateMembershipProduct, dbDeleteMembershipProduct,
  dbAddMembership, dbUpdateMembership, dbDeleteMembership,
  dbAddPtProduct, dbUpdatePtProduct, dbDeletePtProduct,
  dbAddPtAssignment, dbUpdatePtAssignment,
  dbAddPtLog, dbUpdatePtLog, dbDeletePtLog,
  dbUpdateRenewalRequest,
} from '../lib/db';
import { toCamel } from '../utils/helpers';

const MembershipContext = createContext(null);

export const MembershipProvider = ({ children }) => {
  const { gymId, useDB, isDemoMode, reloadTrigger } = useGymInfra();
  const { showToast } = useUI();

  const [membershipProducts, setMembershipProducts] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [ptProducts, setPtProducts] = useState([]);
  const [ptAssignments, setPtAssignments] = useState([]);
  const [ptLogs, setPtLogs] = useState([]);
  const [renewalRequests, setRenewalRequests] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // DB 로드
  useEffect(() => {
    if (useDB && gymId) {
      (async () => {
        try {
          const [
            { data: mp }, { data: ms }, { data: pp },
            { data: pa }, { data: pl }, { data: rr },
          ] = await Promise.all([
            supabase.from('membership_products').select('*').eq('gym_id', gymId),
            supabase.from('memberships').select('*').eq('gym_id', gymId),
            supabase.from('pt_products').select('*').eq('gym_id', gymId),
            supabase.from('pt_assignments').select('*').eq('gym_id', gymId),
            supabase.from('pt_logs').select('*').eq('gym_id', gymId),
            supabase.from('renewal_requests').select('*, members(name, phone)').eq('gym_id', gymId).order('created_at', { ascending: false }),
          ]);
          if (mp) {
            setMembershipProducts(toCamel(mp).map(p => ({
              ...p,
              days: p.durationDays,
            })));
          }
          if (ms) setMemberships(toCamel(ms));
          if (pp) setPtProducts(toCamel(pp));
          if (pa) setPtAssignments(toCamel(pa));
          if (pl) setPtLogs(toCamel(pl));
          if (rr) {
            setRenewalRequests(toCamel(rr).map(r => ({
              ...r,
              memberName: r.members?.name,
              memberPhone: r.members?.phone,
            })));
          }
        } catch (e) {
          console.error('MembershipContext DB 로드 실패:', e);
        } finally {
          setLoaded(true);
        }
      })();
    } else if (isDemoMode) {
      setMembershipProducts(storage.get(STORAGE_KEYS.MEMBERSHIP_PRODUCTS) || []);
      setMemberships(storage.get(STORAGE_KEYS.MEMBERSHIPS) || []);
      setPtProducts(storage.get(STORAGE_KEYS.PT_PRODUCTS) || []);
      setPtAssignments(storage.get(STORAGE_KEYS.PT_ASSIGNMENTS) || []);
      setPtLogs(storage.get(STORAGE_KEYS.PT_LOGS) || []);
      setLoaded(true);
    } else {
      setLoaded(true);
    }
  }, [useDB, gymId, isDemoMode, reloadTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // PT 데이터만 재로드
  const refreshPtData = useCallback(async () => {
    if (!gymId) return;
    const [{ data: asgns }, { data: logs }] = await Promise.all([
      supabase.from('pt_assignments').select('*').eq('gym_id', gymId),
      supabase.from('pt_logs').select('*').eq('gym_id', gymId),
    ]);
    if (asgns) setPtAssignments(toCamel(asgns));
    if (logs) setPtLogs(toCamel(logs));
  }, [gymId]);

  // ================================================================
  // 회원권 상품 CRUD
  // ================================================================
  const addMembershipProduct = useCallback(async (data) => {
    if (useDB) {
      try {
        const newProduct = await dbAddMembershipProduct(gymId, data);
        setMembershipProducts(prev => [...prev, newProduct]);
        showToast('회원권 상품이 등록되었습니다.');
        return newProduct;
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const newProduct = { ...data, id: generateId() };
      const updated = [...membershipProducts, newProduct];
      storage.set(STORAGE_KEYS.MEMBERSHIP_PRODUCTS, updated);
      setMembershipProducts(updated);
      showToast('회원권 상품이 등록되었습니다.');
      return newProduct;
    }
  }, [useDB, gymId, membershipProducts, showToast]);

  const updateMembershipProduct = useCallback(async (id, data) => {
    if (useDB) {
      try {
        await dbUpdateMembershipProduct(id, data);
        setMembershipProducts(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
        showToast('회원권 상품이 수정되었습니다.');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = membershipProducts.map(p => p.id === id ? { ...p, ...data } : p);
      storage.set(STORAGE_KEYS.MEMBERSHIP_PRODUCTS, updated);
      setMembershipProducts(updated);
      showToast('회원권 상품이 수정되었습니다.');
    }
  }, [useDB, membershipProducts, showToast]);

  const deleteMembershipProduct = useCallback(async (id) => {
    if (useDB) {
      try {
        await dbDeleteMembershipProduct(id);
        setMembershipProducts(prev => prev.filter(p => p.id !== id));
        showToast('회원권 상품이 삭제되었습니다.', 'error');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = membershipProducts.filter(p => p.id !== id);
      storage.set(STORAGE_KEYS.MEMBERSHIP_PRODUCTS, updated);
      setMembershipProducts(updated);
      showToast('회원권 상품이 삭제되었습니다.', 'error');
    }
  }, [useDB, membershipProducts, showToast]);

  // ================================================================
  // 회원권 CRUD
  // ================================================================
  const addMembership = useCallback(async (data) => {
    if (useDB) {
      try {
        const newMembership = await dbAddMembership(gymId, data);
        setMemberships(prev => [...prev, newMembership]);
        showToast('회원권이 부여되었습니다.');
        return newMembership;
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const newMembership = { ...data, id: generateId(), createdAt: new Date().toISOString().split('T')[0] };
      const updated = [...memberships, newMembership];
      storage.set(STORAGE_KEYS.MEMBERSHIPS, updated);
      setMemberships(updated);
      showToast('회원권이 부여되었습니다.');
      return newMembership;
    }
  }, [useDB, gymId, memberships, showToast]);

  const updateMembership = useCallback(async (id, data) => {
    if (useDB) {
      try {
        await dbUpdateMembership(id, data);
        setMemberships(prev => prev.map(m => m.id === id ? { ...m, ...data } : m));
        showToast('회원권이 수정되었습니다.');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = memberships.map(m => m.id === id ? { ...m, ...data } : m);
      storage.set(STORAGE_KEYS.MEMBERSHIPS, updated);
      setMemberships(updated);
      showToast('회원권이 수정되었습니다.');
    }
  }, [useDB, memberships, showToast]);

  const deleteMembership = useCallback(async (id) => {
    if (useDB) {
      try {
        await dbDeleteMembership(id);
        setMemberships(prev => prev.filter(m => m.id !== id));
        showToast('회원권이 삭제되었습니다.', 'error');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = memberships.filter(m => m.id !== id);
      storage.set(STORAGE_KEYS.MEMBERSHIPS, updated);
      setMemberships(updated);
      showToast('회원권이 삭제되었습니다.', 'error');
    }
  }, [useDB, memberships, showToast]);

  const freezeMembership = useCallback(async (id, reason = '') => {
    const ms = memberships.find(m => m.id === id);
    if (!ms || ms.frozenAt) { showToast('이미 정지 중인 회원권입니다.', 'error'); return; }
    const frozenAt = new Date().toISOString().split('T')[0];
    const patch = { frozenAt, freezeReason: reason, status: 'frozen' };
    if (useDB) {
      try {
        await dbUpdateMembership(id, patch);
        setMemberships(prev => prev.map(m => m.id === id ? { ...m, ...patch } : m));
        showToast('회원권이 일시정지되었습니다.');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = memberships.map(m => m.id === id ? { ...m, ...patch } : m);
      storage.set(STORAGE_KEYS.MEMBERSHIPS, updated);
      setMemberships(updated);
      showToast('회원권이 일시정지되었습니다.');
    }
  }, [useDB, memberships, showToast]);

  const unfreezeMembership = useCallback(async (id) => {
    const ms = memberships.find(m => m.id === id);
    if (!ms || !ms.frozenAt) { showToast('정지 중인 회원권이 아닙니다.', 'error'); return; }
    const frozenDays = Math.ceil((new Date() - new Date(ms.frozenAt)) / 86400000);
    const newEnd = new Date(ms.endDate);
    newEnd.setDate(newEnd.getDate() + frozenDays);
    const newEndDate = newEnd.toISOString().split('T')[0];
    const today = new Date().toISOString().split('T')[0];
    const freezeHistory = [...(ms.freezeHistory || []), { frozenAt: ms.frozenAt, unfrozenAt: today, days: frozenDays, reason: ms.freezeReason }];
    const patch = { endDate: newEndDate, frozenAt: null, freezeReason: null, status: 'active', freezeHistory };
    if (useDB) {
      try {
        await dbUpdateMembership(id, patch);
        setMemberships(prev => prev.map(m => m.id === id ? { ...m, ...patch } : m));
        showToast(`회원권이 재개되었습니다. 만료일이 ${frozenDays}일 연장되었습니다.`);
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = memberships.map(m => m.id === id ? { ...m, ...patch } : m);
      storage.set(STORAGE_KEYS.MEMBERSHIPS, updated);
      setMemberships(updated);
      showToast(`회원권이 재개되었습니다. 만료일이 ${frozenDays}일 연장되었습니다.`);
    }
  }, [useDB, memberships, showToast]);

  // ================================================================
  // PT 상품 CRUD
  // ================================================================
  const addPtProduct = useCallback(async (data) => {
    if (useDB) {
      try {
        const newProduct = await dbAddPtProduct(gymId, data);
        setPtProducts(prev => [...prev, newProduct]);
        showToast('PT 상품이 등록되었습니다.');
        return newProduct;
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const newProduct = { ...data, id: generateId() };
      const updated = [...ptProducts, newProduct];
      storage.set(STORAGE_KEYS.PT_PRODUCTS, updated);
      setPtProducts(updated);
      showToast('PT 상품이 등록되었습니다.');
      return newProduct;
    }
  }, [useDB, gymId, ptProducts, showToast]);

  const updatePtProduct = useCallback(async (id, data) => {
    if (useDB) {
      try {
        await dbUpdatePtProduct(id, data);
        setPtProducts(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
        showToast('PT 상품이 수정되었습니다.');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = ptProducts.map(p => p.id === id ? { ...p, ...data } : p);
      storage.set(STORAGE_KEYS.PT_PRODUCTS, updated);
      setPtProducts(updated);
      showToast('PT 상품이 수정되었습니다.');
    }
  }, [useDB, ptProducts, showToast]);

  const deletePtProduct = useCallback(async (id) => {
    if (useDB) {
      try {
        await dbDeletePtProduct(id);
        setPtProducts(prev => prev.filter(p => p.id !== id));
        showToast('PT 상품이 삭제되었습니다.', 'error');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = ptProducts.filter(p => p.id !== id);
      storage.set(STORAGE_KEYS.PT_PRODUCTS, updated);
      setPtProducts(updated);
      showToast('PT 상품이 삭제되었습니다.', 'error');
    }
  }, [useDB, ptProducts, showToast]);

  // ================================================================
  // PT 부여 CRUD
  // ================================================================
  const addPtAssignment = useCallback(async (data) => {
    if (useDB) {
      try {
        const newAss = await dbAddPtAssignment(gymId, data);
        setPtAssignments(prev => [...prev, newAss]);
        showToast('PT가 부여되었습니다.');
        return newAss;
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const newAssignment = { ...data, id: generateId(), createdAt: new Date().toISOString().split('T')[0] };
      const updated = [...ptAssignments, newAssignment];
      storage.set(STORAGE_KEYS.PT_ASSIGNMENTS, updated);
      setPtAssignments(updated);
      showToast('PT가 부여되었습니다.');
      return newAssignment;
    }
  }, [useDB, gymId, ptAssignments, showToast]);

  const updatePtAssignment = useCallback(async (id, data) => {
    if (useDB) {
      try {
        await dbUpdatePtAssignment(id, data);
        setPtAssignments(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = ptAssignments.map(p => p.id === id ? { ...p, ...data } : p);
      storage.set(STORAGE_KEYS.PT_ASSIGNMENTS, updated);
      setPtAssignments(updated);
    }
  }, [useDB, ptAssignments, showToast]);

  // ================================================================
  // PT 일지 CRUD
  // ================================================================
  const addPtLog = useCallback(async (data) => {
    if (useDB) {
      try {
        const newLog = await dbAddPtLog(gymId, data);
        setPtLogs(prev => [...prev, newLog]);
        const assignment = ptAssignments.find(a => a.id === data.ptAssignmentId);
        if (assignment) {
          const newUsed = (assignment.usedSessions || 0) + 1;
          const status = newUsed >= assignment.totalSessions ? 'completed' : 'active';
          await dbUpdatePtAssignment(data.ptAssignmentId, { usedSessions: newUsed, status });
          setPtAssignments(prev => prev.map(a => a.id === data.ptAssignmentId ? { ...a, usedSessions: newUsed, status } : a));
        }
        showToast('PT 일지가 등록되었습니다.');
        return newLog;
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const newLog = { ...data, id: generateId() };
      const updated = [...ptLogs, newLog];
      storage.set(STORAGE_KEYS.PT_LOGS, updated);
      setPtLogs(updated);
      const assignment = ptAssignments.find(a => a.id === data.ptAssignmentId);
      if (assignment) {
        const newUsed = (assignment.usedSessions || 0) + 1;
        const status = newUsed >= assignment.totalSessions ? 'completed' : 'active';
        const updatedAss = ptAssignments.map(a => a.id === data.ptAssignmentId ? { ...a, usedSessions: newUsed, status } : a);
        storage.set(STORAGE_KEYS.PT_ASSIGNMENTS, updatedAss);
        setPtAssignments(updatedAss);
      }
      showToast('PT 일지가 등록되었습니다.');
      return newLog;
    }
  }, [useDB, gymId, ptLogs, ptAssignments, showToast]);

  const updatePtLog = useCallback(async (id, data) => {
    if (useDB) {
      try {
        await dbUpdatePtLog(id, data);
        setPtLogs(prev => prev.map(l => l.id === id ? { ...l, ...data } : l));
        showToast('PT 일지가 수정되었습니다.');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = ptLogs.map(l => l.id === id ? { ...l, ...data } : l);
      storage.set(STORAGE_KEYS.PT_LOGS, updated);
      setPtLogs(updated);
      showToast('PT 일지가 수정되었습니다.');
    }
  }, [useDB, ptLogs, showToast]);

  const deletePtLog = useCallback(async (id) => {
    const log = ptLogs.find(l => l.id === id);
    if (useDB) {
      try {
        await dbDeletePtLog(id);
        setPtLogs(prev => prev.filter(l => l.id !== id));
        if (log) {
          const assignment = ptAssignments.find(a => a.id === log.ptAssignmentId);
          if (assignment) {
            const newUsed = Math.max(0, (assignment.usedSessions || 0) - 1);
            const newStatus = newUsed >= assignment.totalSessions ? 'completed' : 'active';
            await dbUpdatePtAssignment(log.ptAssignmentId, { usedSessions: newUsed, status: newStatus });
            setPtAssignments(prev => prev.map(a => a.id === log.ptAssignmentId ? { ...a, usedSessions: newUsed, status: newStatus } : a));
          }
        }
        showToast('PT 일지가 삭제되었습니다.', 'error');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = ptLogs.filter(l => l.id !== id);
      storage.set(STORAGE_KEYS.PT_LOGS, updated);
      setPtLogs(updated);
      if (log) {
        const assignment = ptAssignments.find(a => a.id === log.ptAssignmentId);
        if (assignment) {
          const newUsed = Math.max(0, (assignment.usedSessions || 0) - 1);
          const newStatus = newUsed >= assignment.totalSessions ? 'completed' : 'active';
          const updatedAss = ptAssignments.map(a => a.id === log.ptAssignmentId ? { ...a, usedSessions: newUsed, status: newStatus } : a);
          storage.set(STORAGE_KEYS.PT_ASSIGNMENTS, updatedAss);
          setPtAssignments(updatedAss);
        }
      }
      showToast('PT 일지가 삭제되었습니다.', 'error');
    }
  }, [useDB, ptLogs, ptAssignments, showToast]);

  // ================================================================
  // 연장 신청 처리
  // ================================================================
  const approveRenewal = useCallback(async (id, processedNote = '') => {
    if (!useDB) return;
    try {
      const patch = { status: 'approved', processedAt: new Date().toISOString(), processedNote };
      await dbUpdateRenewalRequest(id, patch);
      setRenewalRequests(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
      showToast('연장 신청이 승인되었습니다.');
    } catch (e) { showToast(e.message, 'error'); }
  }, [useDB, showToast]);

  const rejectRenewal = useCallback(async (id, processedNote = '') => {
    if (!useDB) return;
    try {
      const patch = { status: 'rejected', processedAt: new Date().toISOString(), processedNote };
      await dbUpdateRenewalRequest(id, patch);
      setRenewalRequests(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
      showToast('연장 신청이 거절되었습니다.', 'error');
    } catch (e) { showToast(e.message, 'error'); }
  }, [useDB, showToast]);

  return (
    <MembershipContext.Provider value={{
      membershipProducts, addMembershipProduct, updateMembershipProduct, deleteMembershipProduct,
      memberships, addMembership, updateMembership, deleteMembership, freezeMembership, unfreezeMembership,
      ptProducts, addPtProduct, updatePtProduct, deletePtProduct,
      ptAssignments, addPtAssignment, updatePtAssignment, refreshPtData,
      ptLogs, addPtLog, updatePtLog, deletePtLog,
      renewalRequests, approveRenewal, rejectRenewal,
      _membershipLoaded: loaded,
    }}>
      {children}
    </MembershipContext.Provider>
  );
};

export const useMemberships = () => {
  const ctx = useContext(MembershipContext);
  if (!ctx) throw new Error('useMemberships must be used within MembershipProvider');
  return ctx;
};
